import React, {Fragment, useEffect, useRef, useState} from 'react';
import {Badge, Col, FormGroup, Input, Label, Row} from "reactstrap";
import Select from 'react-select'
import {SellerImageUploader} from "../sellerImageUploader";
import ImageUpload from "image-upload-react";
import {useSelector} from "react-redux";
import { Tooltip } from 'reactstrap';


// Mirrors BrandImageResizer (backend) in the browser so the preview shows exactly what will be
// stored: 'cover' = scale to fill + centre-crop, 'contain' = scale to fit + pad (no crop).
// The ORIGINAL file is still what gets uploaded; this is preview only.
// Same rule as BrandImageResizer::solidEdgeColour(): if all four corners are opaque and nearly the
// same colour, the logo has a solid background and the padding is filled with it (seamless).
const solidEdgeColour = (img, sw, sh) => {
    const c = document.createElement('canvas');
    c.width = 1;
    c.height = 1;
    const ctx = c.getContext('2d');
    const corners = [[0, 0], [sw - 1, 0], [0, sh - 1], [sw - 1, sh - 1]].map(([x, y]) => {
        ctx.clearRect(0, 0, 1, 1);
        ctx.drawImage(img, x, y, 1, 1, 0, 0, 1, 1);
        return ctx.getImageData(0, 0, 1, 1).data;
    });
    if (corners.some(p => p[3] < 240)) return null;
    for (let ch = 0; ch < 3; ch++) {
        const v = corners.map(p => p[ch]);
        if (Math.max(...v) - Math.min(...v) > 12) return null;
    }
    const avg = ch => Math.round(corners.reduce((s, p) => s + p[ch], 0) / 4);
    return `rgb(${avg(0)},${avg(1)},${avg(2)})`;
};

const renderSavedPreview = (file, target, fit) => new Promise((resolve, reject) => {
    const src = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
        const { w: tw, h: th } = target;
        const sw = img.naturalWidth, sh = img.naturalHeight;
        const canvas = document.createElement('canvas');
        canvas.width = tw;
        canvas.height = th;
        const ctx = canvas.getContext('2d');
        const isJpeg = /jpe?g/i.test(file.type);
        ctx.imageSmoothingQuality = 'high';
        if (fit === 'contain') {
            const edge = solidEdgeColour(img, sw, sh);
            if (edge || isJpeg) { ctx.fillStyle = edge || '#fff'; ctx.fillRect(0, 0, tw, th); }
            const s = Math.min(tw / sw, th / sh);
            const w = Math.round(sw * s), h = Math.round(sh * s);
            ctx.drawImage(img, 0, 0, sw, sh, Math.floor((tw - w) / 2), Math.floor((th - h) / 2), w, h);
        } else {
            const s = Math.max(tw / sw, th / sh);
            const cw = Math.min(sw, Math.round(tw / s)), ch = Math.min(sh, Math.round(th / s));
            ctx.drawImage(img, Math.floor((sw - cw) / 2), Math.floor((sh - ch) / 2), cw, ch, 0, 0, tw, th);
        }
        URL.revokeObjectURL(src);
        resolve(canvas.toDataURL(isJpeg ? 'image/jpeg' : 'image/png', 0.9));
    };
    img.onerror = () => { URL.revokeObjectURL(src); reject(new Error('Could not read image')); };
    img.src = src;
});

// Styling for image-upload-react (its inner box has no class, so it is targeted by position):
// hide its white-on-white arrow, and show the preview at the stored aspect ratio, centred, with
// a frame so the saved edges are visible.
const uploaderCss = `
.brand-uploader { position: relative; }
.brand-uploader > div:last-child > div:last-child { transition: border-color .15s, background-color .15s; }
.brand-uploader.is-empty:hover > div:last-child > div:last-child { border-color: #7366ff !important; background-color: #f5f4ff !important; }
.brand-uploader.is-empty svg { display: none; }
.brand-uploader.has-image > div:last-child > div:last-child { background-color: #f1f2f6 !important; border-style: solid !important; }
.brand-uploader.has-image img {
    position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
    width: auto !important; height: auto !important; max-width: calc(100% - 28px); max-height: calc(100% - 28px);
    object-fit: contain !important; border-radius: 2px; outline: 1px solid rgba(44,50,63,.12);
    box-shadow: 0 6px 18px rgba(44,50,63,.18);
}
.brand-uploader.fit-contain.has-image img {
    background-color: #fff;
    background-image: linear-gradient(45deg,#e9e9ee 25%,transparent 25%),linear-gradient(-45deg,#e9e9ee 25%,transparent 25%),
        linear-gradient(45deg,transparent 75%,#e9e9ee 75%),linear-gradient(-45deg,transparent 75%,#e9e9ee 75%);
    background-size: 12px 12px; background-position: 0 0,0 6px,6px -6px,-6px 0;
}
.brand-uploader .bu-overlay { position: absolute; inset: 0; z-index: 100; pointer-events: none;
    display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 0 16px; }
.brand-uploader .bu-frame { border: 2px dashed #b9b3ff; border-radius: 4px; display: flex; align-items: center;
    justify-content: center; color: #7366ff; margin-bottom: 10px; background: #fff; }
.brand-uploader .bu-title { font-weight: 600; color: #2c323f; font-size: 14px; }
.brand-uploader .bu-title span { color: #7366ff; }
.brand-uploader .bu-pill { display: inline-block; margin: 6px 0 4px; padding: 2px 10px; border-radius: 999px;
    background: #eeedff; color: #5a4fe0; font-weight: 600; font-size: 12px; letter-spacing: .3px; }
.brand-uploader .bu-caption { font-size: 11.5px; color: #8a8f99; }
.brand-uploader .bu-badge { position: absolute; left: 8px; bottom: 8px; z-index: 100; pointer-events: none;
    background: rgba(44,50,63,.78); color: #fff; font-size: 11px; padding: 3px 8px; border-radius: 4px; }
.brand-uploader .bu-badge i { margin-right: 4px; color: #8fe3b0; }
.brand-uploader .bu-error { font-size: 12px; color: #dc3545; margin-top: 4px; }
`;

const BrandImageField = ({ label, tooltipId, tooltip, tooltipOpen, toggleTooltip, errorMessages, uploaderRef,
                           imageSrc, setImageSrc, onFile, onDelete, target, frame, fit, caption }) => {
    const [previewError, setPreviewError] = useState(null);
    const size = `${target.w} × ${target.h}`;

    const handleSelect = e => {
        const file = e.target.files[0];
        if (!file) return;
        setPreviewError(null);
        renderSavedPreview(file, target, fit)
            .then(preview => onFile(file, preview))
            .catch(() => {
                // Still let the upload go through; the server validates and resizes.
                setPreviewError('Preview unavailable for this file.');
                onFile(file, URL.createObjectURL(file));
            });
    };

    return (
        <FormGroup>
            <Label className="form-label">{label}</Label>
            <span className="mt-3 mr-2 ml-1" id={tooltipId}>
                <i aria-hidden="true" className="fa fa-exclamation-circle"></i>
            </span>
            <Tooltip placement='right' isOpen={tooltipOpen} target={tooltipId} toggle={toggleTooltip}
                     style={{ fontSize: '12px', maxWidth: '900px' }}>
                {tooltip}
            </Tooltip>
            {errorMessages.filter(Boolean).map((m, i) => <div key={i} className="text-black-50">{m}</div>)}
            <div className={`brand-uploader fit-${fit} ${imageSrc ? 'has-image' : 'is-empty'}`}>
                {/* pointer-events: none on the overlay keeps the whole box clickable for the file input */}
                {!imageSrc && <div className="bu-overlay">
                    <div className="bu-frame" style={{ width: frame.w, height: frame.h }}>
                        <i className="fa fa-cloud-upload" aria-hidden="true" style={{ fontSize: Math.min(frame.w, frame.h) * 0.55 }}></i>
                    </div>
                    <div className="bu-title"><span>Click to upload</span> or drag an image here</div>
                    <div className="bu-pill">{size} px</div>
                    <div className="bu-caption">{caption}</div>
                </div>}
                {imageSrc && <div className="bu-badge"><i className="fa fa-check" aria-hidden="true"></i>Preview · saved as {size}</div>}
                <ImageUpload
                    handleImageSelect={handleSelect}
                    ref={uploaderRef}
                    imageSrc={imageSrc}
                    setImageSrc={setImageSrc}
                    style={{
                        width: 'auto',
                        height: '220px',
                        marginTop: 0,
                        borderRadius: '8px',
                        border: '2px dashed #d6d3f0',
                        backgroundColor: '#fafaff',
                        boxShadow: 'none',
                        overflow: 'hidden',
                    }}
                    deleteIcon={
                        <span className="mt-3 mr-2" onClick={onDelete}>
                            <i aria-hidden="true" className="fa fa-times-circle fa-2x"></i>
                        </span>
                    }
                />
            </div>
            {previewError && <div className="bu-error" style={{ color: '#dc3545', fontSize: 12, marginTop: 4 }}>{previewError}</div>}
        </FormGroup>
    );
};

const BrandForm = (props) => {
    const { errors, control, userRoles, register,setValue,trigger,getValues,brands } = props;
    const [dialCode, setDialCode] = useState('');
    const sellerState = useSelector(({ seller }) => seller.seller);
    const [selectedImage,setSelectedImage]=useState(getValues('imageFile'));
    const [selectedImageLogo,setSelectedImageLogo]=useState(getValues('imageFileLogo'));
    const imageUploaderRef= useRef(null);
    const imageUploaderRefLogo= useRef(null);

    const [tooltipOpen, setTooltipOpen] = useState(false);
    const toggle = () => setTooltipOpen(!tooltipOpen);
    const [tooltipOpen1, setTooltipOpen1] = useState(false);
    const toggle1 = () => setTooltipOpen1(!tooltipOpen1);

    const handleDeleteImage = () => {
        setValue('image', null);
        setValue('imageFile', null);
        setSelectedImage(null);
        const input = document.getElementById("hiddenFileInput1");
        input.value = "true";
    }

    const handleDeleteLogoImage = () => {
        setValue('logoImage', null);
        setValue('imageFileLogo', null);
        setSelectedImageLogo(null);
        const input = document.getElementById("hiddenFileInput2");
        input.value = "true";
    }

    return (
        <Fragment>
            <Row> 
                <Col md={6}>
                    <FormGroup>
                        <Label className="form-label">Brand Name<span style={{color:"red"}}>*</span></Label>
                        <input name="brand" type="text" {...register('brand')} className={`form-control ${errors.brand ? 'is-invalid' : ''}`} />
                        <div className="invalid-feedback">{errors.brand?.message}</div>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label className="form-label">Shopify Collection ID</Label>
                        <input name="collectionId" type="text" {...register('collectionId')} className={`form-control ${errors.collectionId ? 'is-invalid' : ''}`} />
                        <div className="invalid-feedback">{errors.collectionId?.message}</div>
                    </FormGroup>
                </Col>
                <Col md={6} style={{zIndex:"1000"}}>
                     <FormGroup>
                        <Label className="form-label">Country<span style={{color:"red"}}>*</span></Label>
                        <Select placeholder="United Kingdom" getOptionLabel ={(option)=>option.name} defaultValue={getValues('country')}
                                getOptionValue ={(option)=>option.id} {...register("country")} onChange={(e)=>{
                            setValue('country',e);
                            setValue('dialCode',e.dial_code);
                            trigger('country');
                        }} className={` ${errors.country ? 'is-invalid' : ''}`} options={sellerState.settings?sellerState.settings.countries:null} />
                        <div className="invalid-feedback">{errors.country?.message}</div>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label className="form-label">City<span style={{color:"red"}}>*</span></Label>
                        <input name="city" type="text" {...register('city')} className={`form-control ${errors.city ? 'is-invalid' : ''}`} />
                        <div className="invalid-feedback">{errors.city?.message}</div>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label className="form-label">Mobile/Telephone number</Label>
                        <FormGroup className="input-group">
                            <span className="input-group-text">{getValues('country')?.dial_code}</span>
                            <input name="mobileNo" type="number" onKeyPress={(event) => {
                                                    if (!/[0-9]/.test(event.key)) {
                                                    event.preventDefault();
                                                    }
                                                }} 
                                    className={`form-control ${errors.mobileNo ? 'is-invalid' : ''}`}
                                    placeholder="Phone Number" {...register('mobileNo')}  />
                            <div className="invalid-feedback">{errors.mobileNo?.message}</div>
                        </FormGroup>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label className="form-label">Email</Label>
                        <input name="email" type="text" {...register('email')} className={`form-control ${errors.email ? 'is-invalid' : ''}`} />
                        <div className="invalid-feedback">{errors.email?.message}</div>
                    </FormGroup>
                </Col>
                   
                <Col md={6}>
                <input name="imageDeleted" type="hidden" id="hiddenFileInput1"  />
                    <BrandImageField
                        label="Banner Image"
                        tooltipId="TooltipExample"
                        tooltip="Recommended 1920 x 768 px (landscape). Other sizes are auto-cropped to fit."
                        tooltipOpen={tooltipOpen}
                        toggleTooltip={toggle}
                        errorMessages={[errors.image?.message, errors.imageFile?.message]}
                        uploaderRef={imageUploaderRef}
                        imageSrc={selectedImage}
                        setImageSrc={setSelectedImage}
                        onFile={(file, preview) => {
                            setValue('image', file);
                            setValue('imageFile', preview);
                            setSelectedImage(preview);
                        }}
                        onDelete={handleDeleteImage}
                        target={{ w: 1920, h: 768 }}
                        frame={{ w: 60, h: 24 }}
                        fit="cover"
                        caption="Landscape · JPG or PNG · other sizes are cropped to fit"
                    />
                </Col>
                <Col md={6}>
                <input name="logoImageDeleted" type="hidden" id="hiddenFileInput2"  />
                    <BrandImageField
                        label="Brand shop Logo"
                        tooltipId="TooltipExample2"
                        tooltip="Recommended 420 x 120 px (landscape). Other sizes are centre-cropped to fill — keep the logo in the middle."
                        tooltipOpen={tooltipOpen1}
                        toggleTooltip={toggle1}
                        errorMessages={[errors.logoImage?.message, errors.imageFileLogo?.message]}
                        uploaderRef={imageUploaderRefLogo}
                        imageSrc={selectedImageLogo}
                        setImageSrc={setSelectedImageLogo}
                        onFile={(file, preview) => {
                            setValue('logoImage', file);
                            setValue('imageFileLogo', preview);
                            setSelectedImageLogo(preview);
                        }}
                        onDelete={handleDeleteLogoImage}
                        target={{ w: 420, h: 120 }}
                        frame={{ w: 70, h: 20 }}
                        fit="cover"
                        caption="Landscape · JPG or PNG · other sizes are centre-cropped to fill"
                    />
                </Col>
            </Row>
            <style>{uploaderCss}</style>


        </Fragment>
    );
}

export default BrandForm;